import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Formik } from 'formik';
import { Typography } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PageHeader from 'src/core/ui/PageHeader';
import Footer from 'src/core/ui/helpers/Footer';
import ContentCardsContainer, { ContentCard, CardTitle } from 'src/core/ui/ContentCard';
import { FormField } from 'src/core/forms/FormField';
import FormHelper from 'src/core/forms/formHelper';
import { CustomDataGrid } from 'src/core/tables/CustomDataGrid';
import { useGetReceivingOrderByWarehouse } from 'src/pages/warehouses/warehouseRequests';
import { useActiveWarehouse, WarehouseSelectionPopup } from 'src/pages/warehouses/WarehouseSelector';
import { receivingAdviceTypes, useReceivingOrderRowsTable } from './useReceivingOrdersTable';
import { useMessageContext } from 'src/shared/support/MessageContext';

function ReceivingOrderFormPage() {
    const [editId, pageTitle] = FormHelper.getEditPageConfig('Receiving Order');
    const [searchParams] = useSearchParams();
    const { warehouseId: activeWarehouseId } = useActiveWarehouse();
    const warehouseId = searchParams.get('warehouseId') || activeWarehouseId;
    const hasWarehouse = warehouseId != null && warehouseId !== '';
    const { rows: response, loading, status } = useGetReceivingOrderByWarehouse(editId, warehouseId);
    const advice = useMemo(() => getReceivingAdvice(response, editId, warehouseId), [response, editId, warehouseId]);
    const pending = Boolean(editId && hasWarehouse && (loading || status == null || /^(idle|loading|pending)$/i.test(String(status))));
    const initialValues = useMemo(() => advice ? { ...FormHelper.mapToInitialValues(advice), Id: advice.Id ?? advice.ReceivingAdviceId, ReceivingAdviceType: receivingAdviceTypes[String(advice.ReceivingAdviceTypeId)] ?? String(advice.ReceivingAdviceTypeId ?? '') } : {}, [advice]);
    const [rowColumns, rowTableSettings] = useReceivingOrderRowsTable(advice, status, pending);
    const { updateMessageStatus } = useMessageContext();
    const loadErrorReported = useRef(false);
    useEffect(() => {
        if (pending) { loadErrorReported.current = false; return; }
        if (!editId || !hasWarehouse || advice || loadErrorReported.current || (status !== 'success' && status !== 204)) return;
        loadErrorReported.current = true;
        updateMessageStatus({ requestUrl: '/warehouse/receiving-orders', status: 'error', message: 'Could not load the receiving order.' });
    }, [editId, hasWarehouse, pending, advice, status, updateMessageStatus]);

    return <>
        {!hasWarehouse && <WarehouseSelectionPopup />}
        <Formik initialValues={initialValues} onSubmit={() => {}} enableReinitialize>
            {props => <div>
                <PageHeader
                    title={editId ? pageTitle : 'Receiving Order'}
                    subTitle="View Receiving Advice. Receive products from the Receiving Orders table."
                    initialValues={initialValues}
                />
                {!editId ? (
                    <Typography>Select a receiving order from the table to view it.</Typography>
                ) : (
                    <ContentCardsContainer>
                        <ContentCard cardId="Receiving Advice" cardHeader={<CardTitle icon={<Inventory2OutlinedIcon />}>Receiving Advice</CardTitle>} md={6} formLayout>
                            <FormField fieldProps={{ name: 'Sender', label: 'Sender', md: 12, disabled: true }} formikProps={props} />
                            <FormField fieldProps={{ name: 'DeliveryNoteRef', label: 'Delivery Note Ref', md: 6, disabled: true }} formikProps={props} />
                            <FormField fieldProps={{ name: 'Note', label: 'Note', md: 6, disabled: true }} formikProps={props} />
                        </ContentCard>
                        <ContentCard cardId="Information" cardHeader={<CardTitle icon={<InfoOutlinedIcon />}>Information</CardTitle>} md={6} formLayout>
                            <FormField fieldProps={{ name: 'Id', label: 'Receiving Advice Id', md: 6, disabled: true }} formikProps={props} />
                            <FormField fieldProps={{ name: 'WarehouseId', label: 'Warehouse Id', md: 6, disabled: true }} formikProps={props} />
                            <FormField fieldProps={{ name: 'ExternalDocumentId', label: 'External Document Id', md: 6, disabled: true }} formikProps={props} />
                            <FormField fieldProps={{ name: 'ReceivingAdviceType', label: 'Receiving Advice Type', md: 6, disabled: true }} formikProps={props} />
                            <FormField fieldProps={{ name: 'ItemId', label: 'Item Id', md: 6, disabled: true }} formikProps={props} />
                        </ContentCard>
                        <ContentCard cardId="Receiving Rows" cardHeader={<CardTitle icon={<Inventory2OutlinedIcon />}>Receiving Rows</CardTitle>} md={12} contentPadding={0}>
                            <CustomDataGrid columns={rowColumns} tableSettings={rowTableSettings} />
                        </ContentCard>
                    </ContentCardsContainer>
                )}
            </div>}
        </Formik>
        <Footer />
    </>;
}

export default ReceivingOrderFormPage;

const getReceivingAdvice = (response: any, orderId: string | number | null, warehouseId: string | number | null) => {
    const candidates = Array.isArray(response) ? response : response ? [response] : [];
    const matches = candidates.filter(advice => advice && (advice.Id ?? advice.ReceivingAdviceId) != null && String(advice.Id ?? advice.ReceivingAdviceId) === String(orderId) && (advice.WarehouseId == null || String(advice.WarehouseId) === String(warehouseId)));
    return matches.length === 1 ? matches[0] : null;
};
